import AdminHeader from "components/Headers/AdminHeader";
import { REGEX } from "constants/CommonConstants";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

const initialFields = {
  signatureName: {
    value: "",
    error: "",
  },
  designation: {
    value: "",
    error: "",
  },
  authorizedPersonName: {
    value: "",
    error: "",
  },
  document: {
    value: null,
    error: "",
  },
};

function AddSignature() {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || "add";
  const [formData, setFormData] = useState(initialFields);

  useEffect(() => {
    if (mode === "edit") {
      setFormData({
        signatureName: {
          value: "Director Signature",
          error: "",
        },
        designation: {
          value: "Director",
          error: "",
        },
        authorizedPersonName: {
          value: "Amit Sharma",
          error: "",
        },
        document: {
          value: {
            name: "signature3.png",
            mediaPath: require("assets/img/signatures/signature3.png"),
          },
          error: "",
        },
      });
    }
  }, [mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const getValidationError = (field, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (field) {
      case "signatureName":
        return !trimmedValue
          ? "Please enter signature name"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
            ? "Signature name can contain only letters and spaces"
            : "";

      case "designation":
        return !trimmedValue
          ? "Please enter authorized person designation"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
            ? "Designation can contain only letters and spaces"
            : "";

      case "authorizedPersonName":
        return !trimmedValue
          ? "Please enter authorized person name"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
            ? "Name can contain only letters and spaces"
            : "";

      case "document":
        return !value ? "Please upload a signature" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleDocumentInputChange = (e) => {
    const file = e.target.files[0];

    setFormData((prev) => ({
      ...prev,
      document: {
        value: file,
        error: getValidationError("document", file),
      },
    }));
  };

  const getDocTypeFromName = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();
    if (ext === "pdf") return "pdf";
    if (ext === "doc" || ext === "docx") return "doc";
    return "unknown";
  };

  const getPreviewUrl = (file) => {
    if (!file) return "";

    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    return file.mediaPath || "";
  };

  const handleSave = (e) => {
    e.preventDefault();
    navigate("/admin/signatures");
  };

  const handleOnCancel = () => {
    setFormData(initialFields);
    navigate("/admin/signatures");
  };

  const handlePrevent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      setFormData((prev) => ({
        ...prev,
        document: {
          value: null,
          error: "Only JPG, JPEG, PNG or PDF files are allowed",
        },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      document: {
        value: file,
        error: "",
      },
    }));
  };

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row className="justify-content-center">
          <Col>
            <Card>
              <CardHeader className="d-flex align-items-center">
                <i
                  className="fa fa-chevron-left mr-2 cursor"
                  onClick={() => navigate("/admin/signatures")}
                />
                <h3 className="mb-0">
                  {mode === "edit" ? "Edit Signature" : "Add Signature"}
                </h3>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col md="4" sm="12">
                    <FormGroup>
                      <Label className="form-control-label">
                        Signature Name
                      </Label>
                      <span className="text-danger"> *</span>
                      <Input
                        type="text"
                        name="signatureName"
                        placeholder="Enter signature name"
                        value={formData.signatureName.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          formData.signatureName.error ? "is-invalid" : ""
                        }
                      />
                      {formData.signatureName.error && (
                        <div className="invalid-feedback d-block">
                          {formData.signatureName.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="4" sm="12">
                    <FormGroup>
                      <Label className="form-control-label">
                        Authorized Person Designation
                      </Label>
                      <span className="text-danger"> *</span>
                      <Input
                        type="text"
                        name="designation"
                        placeholder="Enter authorized person designation"
                        value={formData.designation.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          formData.designation.error ? "is-invalid" : ""
                        }
                      />
                      {formData.designation.error && (
                        <div className="invalid-feedback d-block">
                          {formData.designation.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="4" sm="12">
                    <FormGroup>
                      <Label className="form-control-label">
                        Authorized Person Name
                      </Label>
                      <span className="text-danger"> *</span>
                      <Input
                        type="text"
                        name="authorizedPersonName"
                        placeholder="Enter authorized person name"
                        value={formData.authorizedPersonName.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          formData.authorizedPersonName.error
                            ? "is-invalid"
                            : ""
                        }
                        maxLength="10"
                      />
                      {formData.authorizedPersonName.error && (
                        <div className="invalid-feedback d-block">
                          {formData.authorizedPersonName.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="4" sm="12">
                    <FormGroup className="mb-4">
                      <Label className="form-control-label mt-3">
                        Upload Signature
                        <span className="text-danger ml-1"> *</span>
                        <span className="text-muted ml-2">
                          ( JPG / JPEG / PNG / PDF • Max 10 MB)
                        </span>
                      </Label>

                      <div
                        className="border rounded p-5"
                        onDragOver={(e) => handlePrevent(e)}
                        onDragEnter={(e) => handlePrevent(e)}
                        onDrop={(e) => handleDrag(e)}
                      >
                        {formData.document.value ? (
                          <div className="signature-container">
                            <span
                              className="position-absolute remove-icon"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  document: { value: null, error: "" },
                                }))
                              }
                            >
                              <i className="ni ni-fat-remove text-danger mt-1" />
                            </span>

                            {getDocTypeFromName(
                              formData.document.value.name,
                            ) === "pdf" ? (
                              <div className="text-center p-3 border rounded">
                                <i className="ni ni-single-copy-04 text-danger display-3" />
                                <p className="mb-0 small">
                                  {formData.document.value.name}
                                </p>
                              </div>
                            ) : (
                              <img
                                src={getPreviewUrl(formData.document.value)}
                                alt="Preview"
                                className="image_box"
                              />
                            )}
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                            <p className="mb-1 text-muted">
                              Click or drag signature to upload
                            </p>
                            <Label
                              for="documentInput"
                              className="btn btn-simple btn-sm mt-2"
                            >
                              Choose Signature
                            </Label>
                          </div>
                        )}

                        <Input
                          type="file"
                          id="documentInput"
                          accept=".jpg,.jpeg,.png,.pdf"
                          className="d-none"
                          onChange={handleDocumentInputChange}
                        />
                      </div>

                      {formData.document.error && (
                        <small className="text-danger">
                          {formData.document.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col className="text-right">
                    <Button
                      color="secondary"
                      className="mr-2 px-4 py-2"
                      type="button"
                      onClick={handleOnCancel}
                    >
                      Cancel
                    </Button>

                    <Button
                      color="primary"
                      className="btn-deep-blue mr-2 px-4 py-2"
                      type="submit"
                      onClick={(e) => {
                        handleSave(e);
                      }}
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddSignature;
